/**
 * `ContentPageErrorCard` — the shared fetch-error card for the two content
 * `DETAIL_ROUTES` pages (`UserVideosPage`, `ContentDetailsPage`).
 *
 * Both pages rendered byte-identical markup above their list; only the retry
 * handler differed (`refresh()` vs `fetchMedia()`), so that is the single prop.
 * The *condition* under which the card shows stays with each page — they gate on
 * different "did the caller hand us the media already" flags (`initialMedias`
 * vs `initialMedia`) and must not be unified.
 */

import { AlertCircle } from 'lucide-react';

import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';

export interface ContentPageErrorCardProps {
    /** Re-runs the page's own fetch. */
    onRetry: () => void;
}

export const ContentPageErrorCard = ({ onRetry }: ContentPageErrorCardProps) => {
    const { theme } = useTheme();
    const { l10n } = useLocale();

    return (
        <div
            className="mb-6 p-4 border rounded-lg"
            style={{
                backgroundColor: theme.backgroundSecondary,
                borderColor: theme.border,
            }}
        >
            <div className="flex items-center space-x-2" style={{ color: theme.danger }}>
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">{l10n.content.errorFetchingFeed}</span>
            </div>
            <button
                onClick={onRetry}
                className="mt-3 px-4 py-2 rounded"
                style={{ backgroundColor: theme.danger, color: theme.text.primary }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.dangerHover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.danger}
            >
                {l10n.common.tryAgain}
            </button>
        </div>
    );
};
